/**
 * Re-entry guard for a finished assessment attempt.
 *
 * The Case Master and Coding assessment routes create a brand new attempt on
 * every mount, so any route back into one - the browser back button, the
 * forward button, a pasted URL - spends another attempt. On a contest that is
 * the user's only one, so a finished attempt is marked here and the route
 * redirects to its report instead of starting over.
 *
 * A marker is keyed by the case/challenge id (the `cid` in the route) and
 * lives in sessionStorage on purpose: it must not outlive the tab, or a
 * legitimate re-attempt in a later session would be blocked. Launching from
 * the start dialog clears it.
 *
 * This is the client-side half of the guard. It cannot cover a second tab or
 * another device - only the API rejecting a create beyond the contest's
 * allowed attempts can do that.
 */
const createAttemptGuard = (namespace) => {
  const keyFor = (id) => `${namespace}AttemptDone:${id}`;

  return {
    /**
     * Remember that this case/challenge's attempt finished and produced a report.
     * @param {string} id - the `cid` in the route.
     * @param {string} attemptId - attempt to show instead of starting a new one.
     */
    mark: (id, attemptId) => {
      if (!id || !attemptId) return;

      sessionStorage.setItem(keyFor(id), attemptId);
    },

    /**
     * The finished attempt's id, or null when there is none in this tab.
     * @param {string} id - the `cid` in the route.
     * @returns {string|null}
     */
    get: (id) => (id ? sessionStorage.getItem(keyFor(id)) : null),

    /**
     * Drop the marker so a deliberate new run can start.
     * @param {string} id - the `cid` in the route.
     */
    clear: (id) => {
      if (!id) return;

      sessionStorage.removeItem(keyFor(id));
    },
  };
};

const caseMasterGuard = createAttemptGuard('caseMaster');
const codingGuard = createAttemptGuard('coding');

export const markCaseMasterAttemptDone = caseMasterGuard.mark;
export const getCompletedCaseMasterAttempt = caseMasterGuard.get;
export const clearCaseMasterAttemptDone = caseMasterGuard.clear;

export const markCodingAttemptDone = codingGuard.mark;
export const getCompletedCodingAttempt = codingGuard.get;
export const clearCodingAttemptDone = codingGuard.clear;
